import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router';

import { queryClient } from '@/src/lib/react-query/react-query';

import { emailEntriesKeys } from './EmailThreadEntryViewer/api/getEmailEntries';
import { EMAIL_THREAD_PAGE_LIMIT } from './EmailThreadEntryViewer/components/EmailThreadViewer/EmailEntries/EmailEntries';
import { fileDetailsKeys } from './FileEntryViewer/api/getFileDetails';
import { useSyncStaleEntry } from './useSyncStaleEntry';

import { useInbox } from '../../store/inbox.store';
import { InboxEntryType, View, RawFileEntry } from '../../types';
import { SocketData, isRefreshedEmailEntryData } from '../../types/SocketData';
import { isStaleEntry, StaleEntry } from '../../types/StaleEntry';
import useInboxSearchParams from '../../useInboxSearchParams';

export const getStaleFileEntryFromCache = (staleEntry: StaleEntry) => {
  const rawExistingFileEntry = queryClient.getQueryData([
    ...fileDetailsKeys.detail({ fileId: staleEntry.data.rawEntry.id }),
  ]) as RawFileEntry;

  if (rawExistingFileEntry) {
    return {
      rawExistingFileEntry,
      isDeleted: !rawExistingFileEntry.data.trash && staleEntry.data.rawEntry.trash, // moved to trash
      isRestored: rawExistingFileEntry.data.trash && !staleEntry.data.rawEntry.trash,
    };
  }
  return {
    rawExistingFileEntry,
    isDeleted: false,
    isRestored: false,
  };
};

export const getStaleEmailEntryFromCache = ({
  staleEntry,
  projectId,
  view,
}: {
  staleEntry: StaleEntry;
  projectId: string;
  view: View;
}) => {
  const emailEntriesPages = queryClient.getQueryData([
    ...emailEntriesKeys.lists({
      projectId,
      inboxEntryId: staleEntry.data.rawIboxEntry['source-id'],
      trash: view === View.Trash ? true : false,
    }),
    EMAIL_THREAD_PAGE_LIMIT,
  ]) as any;

  const emailEntries =
    emailEntriesPages?.pages.flatMap((page) => page.data.emails.map((r) => r)) || [];
  const rawExistingEmailEntry = emailEntries.find(
    (ee) => ee.id === staleEntry.data.rawEntry.rawEmailEntry.id
  );

  if (rawExistingEmailEntry) {
    return {
      rawExistingEmailEntry,
      isDeleted: !rawExistingEmailEntry.trash && staleEntry.data.rawEntry.rawEmailEntry.trash, // moved to trash
      isRestored: rawExistingEmailEntry.trash && !staleEntry.data.rawEntry.rawEmailEntry.trash,
    };
  }

  return {
    rawExistingEmailEntry,
    isDeleted: false,
    isRestored: false,
  };
};

export const checkStaleEntryFromCache = ({
  staleEntry,
  projectId,
  view,
}: {
  staleEntry: StaleEntry;
  projectId: string;
  view: View;
}) => {
  const staleFileEntry = getStaleFileEntryFromCache(staleEntry);
  const staleEmailEntry = getStaleEmailEntryFromCache({ staleEntry, projectId, view });
  const cachedStaleEntry =
    staleFileEntry.rawExistingFileEntry || staleEmailEntry.rawExistingEmailEntry;

  if (staleEntry.type === InboxEntryType.DOCUMENT) {
    return {
      isDeleted: staleFileEntry.isDeleted,
      isRestored: staleFileEntry.isRestored,
      cachedStaleEntry,
    };
  } else {
    return {
      isDeleted: staleEmailEntry.isDeleted,
      isRestored: staleEmailEntry.isRestored,
      cachedStaleEntry,
    };
  }
};

export const getStaleEntryFromCache = ({
  staleEntry,
  projectId,
  view,
}: {
  staleEntry: StaleEntry;
  projectId: string;
  view: View;
}) => {
  if (staleEntry.type === InboxEntryType.DOCUMENT) {
    const staleFileEntry = getStaleFileEntryFromCache(staleEntry);
    return {
      isDeleted: staleFileEntry.isDeleted,
      isRestored: staleFileEntry.isRestored,
    };
  } else {
    const staleEmailEntry = getStaleEmailEntryFromCache({ staleEntry, projectId, view });
    return {
      isDeleted: staleEmailEntry.isDeleted,
      isRestored: staleEmailEntry.isRestored,
    };
  }
};

type useEntryPanelSocketSubscriptionsProps = {
  inboxEntryId: number;
};

export const useEntryPanelSocketSubscriptions = ({
  inboxEntryId,
}: useEntryPanelSocketSubscriptionsProps) => {
  const {
    socketData,
    setShowStaleEntryToast,
    setSocketData,
    setDeletedFileEntry,
    upsertDeletedEmailEntry,
    upsertUpdatedEmailEntry,
    clearSocket,
  } = useInbox();

  const syncStaleEntry = useSyncStaleEntry();
  const { projectId } = useParams<{ projectId: string }>();
  const { view } = useInboxSearchParams();

  const handleSyncSaleEntryAndClearToast = useCallback(
    (
      { shouldSyncInboxEntry = true }: { shouldSyncInboxEntry?: boolean } = {
        shouldSyncInboxEntry: true,
      }
    ) => {
      syncStaleEntry({ shouldSyncInboxEntry });
      setShowStaleEntryToast(false);
    },
    [syncStaleEntry, setShowStaleEntryToast]
  );

  const handeleDeleted = (socketData: SocketData) => {
    if (socketData.type === InboxEntryType.DOCUMENT) {
      setDeletedFileEntry(socketData.payload['inbox-entry-source-id']);
    } else {
      upsertDeletedEmailEntry(socketData.payload['inbox-email-id']);
    }
  };
  const handeleUpdated = (staleEntry: StaleEntry) => {
    if (staleEntry.type === InboxEntryType.DOCUMENT) {
      return;
    }

    if (!isRefreshedEmailEntryData(staleEntry.data.rawEntry)) {
      return;
    }

    upsertUpdatedEmailEntry(staleEntry.data.rawEntry);
  };

  useEffect(() => {
    if (!socketData) {
      return;
    }

    if (!inboxEntryId) {
      syncStaleEntry({ shouldSyncInboxEntry: false });
      clearSocket();
      return;
    }

    if (inboxEntryId !== socketData.payload['inbox-entry-source-id']) {
      // @TODO: we sync non selected in background, but staleEntry must now be an object becuase a selected entry can be stale after multiple updates
      syncStaleEntry({ shouldSyncInboxEntry: false });
      // @TODO: in syncStaleEntry clear the staleEntry specific object
      setShowStaleEntryToast(false);
      return;
    }

    // Auto refresh for entry panel when same user who made the action
    if (socketData.isSameUser) {
      syncStaleEntry();
      return;
    }

    if (socketData.payload.deleted) {
      handeleDeleted(socketData);
      return;
    }

    if (!isStaleEntry(socketData)) {
      return;
    }

    const { isDeleted } = checkStaleEntryFromCache({ staleEntry: socketData, projectId, view });

    if (isDeleted || socketData.data.rawIboxEntry['permanently-deleted'] === true) {
      handeleDeleted(socketData);

      return;
    }

    handeleUpdated(socketData);

    // For first new emails, we don't show the stale toast
    if (
      socketData.type === InboxEntryType.THREAD &&
      socketData.payload.inserted &&
      socketData.data.rawIboxEntry.thread['total-emails-count'] === 1
    ) {
      return;
    }

    setShowStaleEntryToast(true);
  }, [inboxEntryId, socketData, projectId, view, setSocketData, setShowStaleEntryToast]);

  return {
    onSyncStaleEntry: handleSyncSaleEntryAndClearToast,
  };
};
